import { useState, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import {
  Drawer, Autocomplete, TextField, Checkbox, Button, Chip, Typography,
  Paper, Stack, Box, InputAdornment, IconButton, Snackbar, Alert
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import DoneIcon from '@mui/icons-material/Done'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'

import { constants } from '../../Constants/constants'
import { getString } from '../../helper/util'
import { EditCreateDisplayV2 } from '../../Component/EditCreateDisplay/EditCreateDisplayV2'
import { TempBtn } from '../../Component/Input/TempBtn'
import { RestaurantList } from '../Restaurant/RestaurantList'
import SharedDialog from '../../Component/common/dialog/SharedDialog'
import { GlobalDialog } from '../../Component/GlobalDialog'
import AppVersionRestaurantConfirm from './AppVersionRestaurantConfirm'
import AppVersionList from './AppVersionList'
import AppVersionTemplateList from './AppVersionTemplate/AppVersionTemplateList'
import DeviceVersionList from './components/DeviceVersionList'
import ReleasePickerDialog from './components/AppVersionReleasePicker'
import { useAppVersionRestaurantCreate } from './hooks/useAppVersionRestaurantCreate'
import variables from '../../styles/globalVariables.scss'
import { newGetList } from '../../helper/baseFetch_v2'
import { useStore } from 'react-redux'

const { blackColor } = variables

const normalTextStyle = makeStyles(() => ({
  label: {
    '& .MuiFormLabel-root': { color: blackColor },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: blackColor },
    '& .MuiFormLabel-root.Mui-disabled': { color: 'rgba(18, 18, 18, 0.54)' },
  },
}))

const helperTextStyles = makeStyles(() => ({
  root: { color: 'red !important' },
}))

export const AppVersionRestaurantCreate = ({ deleteMode = false }) => {
  const normalTextStyleClasses = normalTextStyle()
  const helperTextStylesClasses = helperTextStyles()
  const resource = 'appVersionRestaurantPage'

  const store = useStore()
  const {
    savedAppVersionRestaurant, lang, theme,
    inputError,
    addRestaurantMap, setAddRestaurantMap,
    drawerOpen, drawerAppVersionOpen, drawerTemplateOpen,
    drawerType,
    customFilter,
    showDialog, setShowDialog,
    confirmList,
    dialog,
    warningDialog, setWarningDialog,
    onChangeInput, onChangeVersionConfigList,
    handleFolderMap, handleOnClickDrawer,
    onClickCloseDrawer, handleRestUrlId,
    applyTemplate,
    onClickListRow, onClickSave, onClickConfirmSave,
    saveAsTemplateDialog, setSaveAsTemplateDialog, onClickConfirmSaveTemplate,
    snackbar, setSnackbar
  } = useAppVersionRestaurantCreate({ deleteMode })

  const versionConfigList = savedAppVersionRestaurant.versionConfigList ?? []
  const addedDeviceTypes = versionConfigList.map((i) => i.deviceType)

  const isSaveDisabled = useMemo(
    () => {
      return savedAppVersionRestaurant.restUrlIdList?.length === 0 ||
      (!deleteMode && (
        Object.keys(inputError).length > 0 ||
        versionConfigList.length === 0 ||
        !versionConfigList.some((item) => item.shopAppReleaseId?.id || item.shopAppRelease?.id)
      )) ||
      (deleteMode && !versionConfigList.some((item) => item.status === true))
    },
    [savedAppVersionRestaurant.restUrlIdList, deleteMode, inputError, versionConfigList]
  )

  async function onClickSaveAsTemplate() {
    setSaveAsTemplateDialog({ open: true, name: '', saving: false })
  }

  async function onConfirmSaveAsTemplate() {
    onClickConfirmSaveTemplate(versionConfigList, saveAsTemplateDialog.name)
    // setSaveAsTemplateDialog((prev) => ({ ...prev, saving: true }))
    // try {
    //   await saveAsTemplate(versionConfigList, saveAsTemplateDialog.name)
    //   setSaveAsTemplateDialog({ open: false, name: '', saving: false })
    //   // show success snackbar or dialog
    // } catch {
    //   setSaveAsTemplateDialog((prev) => ({ ...prev, saving: false }))
    // }
  }

  const [releaseDialog, setReleaseDialog] = useState({ open: false, versionGroup: null })

  function onClickVersionRow(versionGroup) {
    // called when user clicks a row in AppVersionList drawer
    setReleaseDialog({ open: true, versionGroup })
  }

  function onSelectRelease(release) {
    setReleaseDialog({ open: false, versionGroup: null })
    onClickListRow(release, 'appVersion')
  }

  return (
    <>
      <EditCreateDisplayV2>
        <EditCreateDisplayV2.Header
          resource={resource}
          display={savedAppVersionRestaurant.customId || deleteMode ? 'delete' : 'create'}
          code={savedAppVersionRestaurant?.customId ?? ''}
        />

        <EditCreateDisplayV2.Body>
          {/* ── Restaurant selector ── */}
          <Autocomplete
            open={false}
            forcePopupIcon={false}
            size={constants.SIZE.SMALL}
            multiple
            options={[]}
            renderTags={(value, getTagProps) =>
              (value ?? []).map((option, index) => (
                <Chip key={index} variant="outlined" color="primary" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                className={normalTextStyleClasses.label}
                variant="outlined"
                label={getString('', resource, 'restUrlIdList', theme)}
                disabled
                fullWidth
                readOnly
                onClick={(e) => handleOnClickDrawer(e)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  readOnly: true,
                  endAdornment: (
                    <>
                      {params.InputProps?.endAdornment}
                      <InputAdornment position="end">
                        <IconButton
                          sx={{ position: 'absolute', right: '32px' }}
                          onClick={(e) => handleOnClickDrawer(e)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    </>
                  ),
                }}
              />
            )}
            onChange={(_, value) => onChangeInput('restUrlIdList', value)}
            value={savedAppVersionRestaurant.restUrlIdList ?? []}
          />

          {/* ── Add device type buttons ── */}
          <Stack direction="row" justifyContent={'space-between'} spacing={1} sx={{ mt: 2 }}>
            <Box>
                {['PR', 'KIOSK', 'TV'].map((type) => (
                  <Button
                    sx={{m:1}}
                    key={type}
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={addedDeviceTypes.some((d) => d === type)}
                    onClick={() => handleFolderMap(type)}
                  >
                    {getString(lang, resource, type.toLowerCase(), theme)}
                  </Button>
                ))}
            </Box>
            
            <Box>
                <Button
                  sx={{m:1}}
                  variant="contained"
                  onClick={(e) => handleOnClickDrawer(e, 'template')}
                >
                  {getString(lang, resource, 'template', theme)}
                </Button>
            </Box>
          </Stack>

          {/* ── Device version config ── */}
          {versionConfigList.length > 0 && (
            <DeviceVersionList
              rawData={versionConfigList}
              resource={resource}
              lang={lang}
              theme={theme}
              deleteMode={deleteMode}
              inputError={inputError}
              helperTextStylesClasses={helperTextStylesClasses}
              onChangeVersionConfigList={onChangeVersionConfigList}
              handleFolderMap={handleFolderMap}
              handleOnClickDrawer={handleOnClickDrawer}
            />
          )}
        </EditCreateDisplayV2.Body>

        <EditCreateDisplayV2.Footer>
          {/* <Button variant="outlined" startIcon={<SaveAsIcon />} onClick={onClickSaveAsTemplate}>
            {getString(lang, '', 'saveAsTemplate', theme)}
          </Button> */}
          <Button variant="contained" startIcon={<DoneIcon />} disabled={isSaveDisabled} onClick={onClickSave}>
            {getString(lang, '', 'save', theme)}
          </Button>
        </EditCreateDisplayV2.Footer>
      </EditCreateDisplayV2>

      {/* ── Restaurant Drawer ── */}
      <Drawer classes={{ paper: 'drawer-margin-left' }} anchor="right" open={drawerOpen} onClose={() => onClickCloseDrawer()}>
        <Paper>
          <RestaurantList
            editBtn={false} createBtn={false} btnList={false}
            mutipleSelect selectMap={addRestaurantMap} setSelectMap={setAddRestaurantMap}
            dialogFooterBtn={
              <Stack direction="row" spacing={1}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleRestUrlId}>
                  {getString(lang, '', 'submit', theme)}
                </Button>
                <Button variant="contained" startIcon={<ClearIcon />} onClick={() => onClickCloseDrawer()}>
                  {getString(lang, '', 'close', theme)}
                </Button>
              </Stack>
            }
          />
        </Paper>
      </Drawer>

      {/* ── App Version Drawer ── */}
      <Drawer classes={{ paper: 'drawer-margin-left' }} anchor="right" open={drawerAppVersionOpen} onClose={() => onClickCloseDrawer('appVersion')}>
        <Paper>
          <AppVersionList
            editBtn={false} createBtn={false} btnList={false}
            onClickListRow={onClickVersionRow}
            dialogFooterBtn={
              <Button variant="contained" startIcon={<ClearIcon />} onClick={() => onClickCloseDrawer('appVersion')}>
                {getString(lang, '', 'close', theme)}
              </Button>
            }
          />
          {/* <AppVersionReleasePicker
            buildTarget={drawerType?.buildTarget}         // e.g. 'ANDROID_MODERN'
            onClickListRow={(item) => onClickListRow(item, 'appVersion')}
            dialogFooterBtn={
              <Button
                variant="contained"
                startIcon={<ClearIcon />}
                onClick={() => onClickCloseDrawer('appVersion')}
              >
                {getString(lang, '', 'close', theme)}
              </Button>
            }
          /> */}
          {/* {customFilter && (
            <AppVersionList
              editBtn={false} createBtn={false} btnList={false}
              onClickListRow={(item) => onClickListRow(item, 'appVersion')}
              newListFilters={customFilter}
              dialogFooterBtn={
                <Button variant="contained" startIcon={<ClearIcon />} onClick={() => onClickCloseDrawer('appVersion')}>
                  {getString(lang, '', 'close', theme)}
                </Button>
              }
            />
          )} */}
        </Paper>
      </Drawer>
            
      {/* ── Step 2: pick release from selected version ── */}
      <ReleasePickerDialog
        open={releaseDialog.open}
        onClose={() => setReleaseDialog({ open: false, versionGroup: null })}
        versionGroup={releaseDialog.versionGroup}
        buildTarget={drawerType?.buildTarget}
        onSelect={onSelectRelease}
      />

      <Drawer classes={{ paper: 'drawer-margin-left' }} anchor="right" open={drawerTemplateOpen} onClose={() => onClickCloseDrawer('template')}>
        <Paper>
          {/* {customFilter && ( */}
            <AppVersionTemplateList
              editBtn={false} createBtn={false} btnList={false}
              // onClickListRow={(item) => {console.log(item); onClickListRow(item, 'template')}}
              onClickListRow={async(item) => {
                const tempnewGetList = await newGetList({
                  path: `gulu-console/merchant/shop-app-version-control/template/${item?.id}`,
                  successCallback: (payload) => {
                    applyTemplate(payload)
                    onClickCloseDrawer('template')
                    // setTemplate(payload)
                    // setLoading(false)
                  },
                  store,
                })
                
              }}
              newListFilters={customFilter}
              dialogFooterBtn={
                <Button variant="contained" startIcon={<ClearIcon />} onClick={() => onClickCloseDrawer('template')}>
                  {getString(lang, '', 'close', theme)}
                </Button>
              }
            />
          {/* )} */}
        </Paper>
      </Drawer>

      {/* ── Confirm Dialog ── */}
      <SharedDialog
        maxWidth="md"
        open={showDialog}
        onClose={() => setShowDialog(false)}
        title="Confirm"
        content={<AppVersionRestaurantConfirm data={confirmList} />}
        actions={
          <Stack direction="row" gap={1}>
            <Button variant="contained" onClick={onClickConfirmSave}>Save</Button>
            <Button variant="outlined" onClick={() => setShowDialog(false)}>Cancel</Button>
          </Stack>
        }
      />

      {/* ── Warning Dialog ── */}
      <SharedDialog
        maxWidth="md"
        open={warningDialog.open}
        onClose={() => setWarningDialog({ open: false, dialogContent: null, dialogActions: [] })}
        title="Warning"
        content={
          warningDialog.dialogContent && (
            <Box>
              <Typography color="primary.main" component="span">
                {warningDialog.dialogContent.join(', ')}
              </Typography>
              {' '}have been added but not assigned with any app version.
              <br />
              Please remove these device type(s) or assign at least one app version before saving.
            </Box>
          )
        }
        actions={
          <Button variant="outlined" onClick={() => setWarningDialog({ open: false, dialogContent: null, dialogActions: [] })}>
            Close
          </Button>
        }
      />

      {dialog?.open && <GlobalDialog dialogInfo={dialog} />}

      {/* ── Save as Template Dialog ── */}
      <SharedDialog
        open={saveAsTemplateDialog.open}
        onClose={() => setSaveAsTemplateDialog({ open: false, name: '', saving: false })}
        title="Save as Template"
        content={
          <TextField
            autoFocus
            size="small"
            label="Template Name"
            fullWidth
            value={saveAsTemplateDialog.name}
            onChange={(e) => setSaveAsTemplateDialog((prev) => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 1 }}
          />
        }
        actions={
          <Stack direction="row" gap={1}>
            <Button
              variant="contained"
              disabled={!saveAsTemplateDialog.name.trim() || saveAsTemplateDialog.saving}
              onClick={onConfirmSaveAsTemplate}
            >
              {saveAsTemplateDialog.saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={() => setSaveAsTemplateDialog({ open: false, name: '', saving: false })}>
              Cancel
            </Button>
          </Stack>
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity={snackbar.type || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
